import { useMemo } from "react";
import PlayerAvatar from "../common/PlayerAvatar";
import ClubCrest from "../common/ClubCrest";
import NationalityFlags from "../common/NationalityFlags";
import { getBlockDefs, buildRow, buildHero } from "./metricDefs";
import { positionLabel, marketValueLabel } from "./playerDisplay";

// Compare page — mobile grid, own concept exactly per the
// CompareGridMobile prototype (frames 1i–1m):
//   - sticky player chips (horizontally scrollable) under the page header
//   - metric label as a full row, player values as equal columns below
//     (columns share the width — nothing is cut off; compact numerals at 3–4)
//   - bars under the values
//   - season selector opens as a bottom sheet (SeasonSelector variant=mobile)
// Declared mobile behaviour: the page itself never scrolls horizontally at
// 375px; only the chips row scrolls within itself.

const A = {
  base: "#2283FF",
  dark: "rgb(24,94,184)",
  tint: "rgba(34,131,255,0.085)",
  ring: "inset 0 0 0 1px rgba(34,131,255,0.22)",
  soft: "rgba(34,131,255,0.38)",
};

const Bar = ({ share, isBest, track = "#EDF1F7" }) => (
  <div className="w-full h-1 rounded-sm overflow-hidden" style={{ background: track }}>
    {share !== null && (
      <div
        style={{
          width: `${(share * 100).toFixed(1)}%`,
          height: "100%",
          borderRadius: 2,
          background: isBest ? A.base : A.soft,
        }}
      />
    )}
  </div>
);

const CompareGridMobile = ({
  players,
  statMaps,
  seasonTeamLabels = [],
  mode,
  notice,
  seasonSelector,
  emptySeasonLabel,
  loading,
  onRemove,
  onReset,
  onAddClick,
  addOpen,
  searchNode,
  // expanded/per90 live in the PAGE (shared with the desktop grid) so a
  // loading extra player never unmounts the grid state.
  expanded = {},
  per90 = {},
  onToggleExpanded = () => {},
  onSetPer90 = () => {},
  loadErrors = [],
  onRetry = () => {},
}) => {
  const n = players.length;
  const compact = n > 2;
  const cols = `repeat(${n}, minmax(0,1fr))`;
  const numSize = compact ? 13.5 : 16;
  const heroSize = compact ? 26 : 32;

  const blockDefs = useMemo(() => getBlockDefs(mode), [mode]);
  const hero = useMemo(() => buildHero(statMaps), [statMaps]);

  return (
    <div className="bg-[#F5F7FA]">
      {/* sticky header: title + count + reset + player chips */}
      <div className="sticky top-0 z-[6] bg-white/95 backdrop-blur border-b border-[#E4E9F0] pt-[10px] pb-[10px] pl-3">
        <div className="flex items-center justify-between pr-3 mb-[9px]">
          <div className="text-[16px] font-bold tracking-[-.01em] text-[#0F172A]">Compare</div>
          <div className="flex items-center gap-[10px]">
            {loading && (
              <span className="flex items-center gap-[7px]">
                <span className="w-[11px] h-[11px] rounded-full border-2 border-[#DCE5F0] border-t-[#2283FF] animate-spin" />
              </span>
            )}
            <span className="text-[11px] text-[#8494A8] font-semibold">{n} of 4</span>
            <button type="button" onClick={onReset} className="text-[11.5px] font-semibold text-[#64748B]">
              Reset
            </button>
          </div>
        </div>
        <div className="flex gap-[7px] overflow-x-auto pr-3 pb-[2px]">
          {players.map((p) => (
            <div
              key={p.id}
              className="flex items-center gap-2 pl-[6px] pr-2 py-[6px] border border-[#E7ECF3] rounded-[11px] bg-white flex-shrink-0"
            >
              <div className="relative flex-shrink-0 w-[30px] h-[30px]">
                <PlayerAvatar src={p.imageUrl} name={p.name} className="w-full h-full rounded-full text-[11px]" />
                <ClubCrest name={p.club || ""} logoUrl={p.clubImage} variant="badge" avatarSize={30} />
              </div>
              <div className="flex flex-col gap-[1px]">
                <div className="text-[13px] font-semibold text-[#0F172A] whitespace-nowrap max-w-[120px] truncate" data-no-translate="true">
                  {p.name}
                </div>
                <div className="flex items-center gap-1">
                  <NationalityFlags
                    nationalities={p}
                    max={1}
                    className="w-[15px] h-[11px] object-cover rounded-[2px] flex-shrink-0"
                    wrapperClassName="flex items-center"
                  />
                  {/* Sub-line gains the current market value ("RW · 19 · €45M").
                      Declared mobile behaviour: the chip grows by a few ch and
                      the CHIP ROW scrolls horizontally within itself (it
                      already does) — the page never scrolls sideways and
                      nothing truncates at the "€850k" short format. */}
                  <span className="text-[11px] text-[#5A6879] whitespace-nowrap" data-no-translate="true">
                    {[positionLabel(p), p.age].filter((x) => x !== null && x !== undefined && x !== "").join(" · ")}
                    {marketValueLabel(p.marketValue) && (
                      <span title="Market value">{` · ${marketValueLabel(p.marketValue)}`}</span>
                    )}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => onRemove(p.id)}
                aria-label="Remove"
                className="text-[10px] text-[#A3B0C2] px-[6px] py-2"
              >
                ✕
              </button>
            </div>
          ))}
          {n < 4 && (
            <button
              type="button"
              onClick={onAddClick}
              className="flex items-center gap-[6px] px-[13px] h-[42px] self-center border border-dashed border-[#CFD9E6] rounded-[11px] text-[#8494A8] text-[11.5px] font-semibold flex-shrink-0"
            >
              <span className="text-[13px] leading-none">+</span>
              <span>Add</span>
            </button>
          )}
        </div>
      </div>

      <div className="p-3 flex flex-col gap-[10px]">
        {addOpen && searchNode}

        {seasonSelector}

        {notice && (
          <div className="flex items-start gap-2 px-[13px] py-[11px] bg-[#EFF4FB] border border-[#E1EAF6] rounded-xl">
            <div className="w-[6px] h-[6px] rounded-[2px] bg-[#2283FF] mt-[5px] flex-shrink-0" />
            <div className="text-[11.5px] leading-[1.45] text-[#35507A]">{notice}</div>
          </div>
        )}

        {/* rating hero card */}
        <div className="bg-white border border-[#E4E9F0] rounded-[14px] overflow-hidden">
          <div className="flex items-center justify-between px-[13px] pt-[11px] pb-[9px]">
            <span className="text-[14.5px] font-bold text-[#0F172A]">Rating</span>
            <span className="text-[11.5px] text-[#7A8798]">Ø per match</span>
          </div>
          <div className="grid gap-[7px] px-[13px] pb-[13px]" style={{ gridTemplateColumns: cols }}>
            {hero.map((h, i) => (
              <div
                key={players[i].id}
                className="px-[9px] py-2 rounded-[10px] flex flex-col gap-[6px] items-center"
                style={{
                  background: h.isBest ? A.tint : "#FAFBFD",
                  boxShadow: h.isBest ? A.ring : "none",
                }}
              >
                {loadErrors[i] ? (
                  <div className="w-full px-1 py-[9px] border border-dashed border-[#CFD9E6] rounded-[9px] text-center text-[10px] font-semibold text-[#8494A8] leading-[1.35]">
                    <div>Could not load player data</div>
                    <button
                      type="button"
                      onClick={() => onRetry(players[i].id)}
                      className="mt-[3px] text-[10px] font-semibold text-[#1B6FD6]"
                    >
                      Retry
                    </button>
                  </div>
                ) : h.empty ? (
                  <div className="w-full px-1 py-[9px] border border-dashed border-[#CFD9E6] rounded-[9px] text-center text-[10px] font-semibold text-[#8494A8] leading-[1.35]">
                    {emptySeasonLabel
                      ? `No statistics for ${emptySeasonLabel}`
                      : "No statistics available"}
                  </div>
                ) : h.missing ? (
                  // Stats exist but the rating is NULL (backend defect F1):
                  // plain em-dash, no bar — never the "No statistics" card.
                  <div
                    className="font-bold tracking-[-.02em] leading-none tabular-nums text-[#A3B0C2]"
                    style={{ fontSize: heroSize }}
                    data-no-translate="true"
                  >
                    {h.text}
                  </div>
                ) : (
                  <>
                    <div
                      className="font-bold tracking-[-.02em] leading-none tabular-nums"
                      style={{ fontSize: heroSize, color: h.isBest ? A.dark : "#0F172A" }}
                      data-no-translate="true"
                    >
                      {h.text}
                    </div>
                    <div className="w-full h-[6px] rounded-[3px] overflow-hidden bg-[#E7EDF5]">
                      <div
                        style={{
                          width: `${(h.share * 100).toFixed(1)}%`,
                          height: "100%",
                          borderRadius: 2,
                          background: h.isBest ? A.base : A.soft,
                        }}
                      />
                    </div>
                  </>
                )}
                <div className="text-[10.5px] font-bold tracking-[.04em] text-[#7A8798] uppercase" data-no-translate="true">
                  {(players[i].name || "")
                    .split(/\s+/)
                    .map((w) => w[0])
                    .join("")
                    .slice(0, 2)}
                </div>
                {/* whose stats: season + team of this player's resolved
                    entry. Declared mobile behaviour: truncates with an
                    ellipsis inside its hero cell — never widens the grid. */}
                {seasonTeamLabels[i] && (
                  <div
                    className="w-full text-center text-[9.5px] text-[#8494A8] truncate"
                    title={seasonTeamLabels[i]}
                    data-no-translate="true"
                  >
                    {seasonTeamLabels[i]}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* metric blocks */}
        {blockDefs.map((block) => {
          const isOpen = !!expanded[block.id];
          const isPer90 = !!per90[block.id];
          const hiddenCount = block.rows.filter((r) => r.extra).length;
          const visibleDefs = block.rows.filter((r) => !r.extra || isOpen);
          return (
            <div key={block.id} className="bg-white border border-[#E4E9F0] rounded-[14px] overflow-hidden">
              <div className="flex items-center justify-between px-[13px] py-[11px] bg-[#1B6FD6]">
                <span className="text-[11px] font-bold tracking-[.1em] uppercase text-white">{block.title}</span>
                {block.p90 && (
                  <div className="flex gap-[2px] p-[2px] bg-white/[.12] rounded-[7px]">
                    <button
                      type="button"
                      onClick={() => onSetPer90(block.id, false)}
                      className={`px-2 py-[2px] rounded-[5px] text-[10px] font-bold ${
                        !isPer90 ? "bg-white text-[#1B6FD6]" : "text-[#CFE2FA] font-semibold"
                      }`}
                    >
                      Total
                    </button>
                    <button
                      type="button"
                      onClick={() => onSetPer90(block.id, true)}
                      className={`px-2 py-[2px] rounded-[5px] text-[10px] font-bold ${
                        isPer90 ? "bg-white text-[#1B6FD6]" : "text-[#CFE2FA] font-semibold"
                      }`}
                    >
                      /90
                    </button>
                  </div>
                )}
              </div>
              {visibleDefs.map((def) => {
                const row = buildRow(def, statMaps, isPer90);
                return (
                  <div key={def.id} className="px-[13px] pt-[9px] pb-[11px] border-b border-[#F4F7FA] last:border-b-0">
                    <div className="flex items-baseline gap-[6px] mb-[7px]">
                      <span className="text-[13px] font-semibold text-[#1F2A37]">{row.label}</span>
                      {row.note && (
                        <span className="text-[10px] text-[#7A8798] font-bold tracking-[.04em]">{row.note}</span>
                      )}
                    </div>
                    <div className="grid gap-[7px]" style={{ gridTemplateColumns: cols }}>
                      {row.cells.map((c, i) => (
                        <div
                          key={players[i].id}
                          className="px-[6px] py-1 rounded-[7px] flex flex-col gap-[5px] items-center"
                          style={{
                            background: c.isBest ? A.tint : "transparent",
                            boxShadow: c.isBest ? A.ring : "none",
                          }}
                        >
                          <div className="flex items-baseline gap-[3px]">
                            <span
                              className="tabular-nums leading-[1.1]"
                              style={{
                                fontSize: numSize,
                                fontWeight: c.isBest ? 700 : 500,
                                color: c.missing ? "#A3B0C2" : c.isBest ? A.dark : "#0F172A",
                              }}
                              data-no-translate="true"
                            >
                              {c.text}
                            </span>
                            {c.sub && (
                              <span className="text-[10.5px] text-[#7A8798] font-semibold" data-no-translate="true">
                                {c.sub}
                              </span>
                            )}
                          </div>
                          {!c.missing && <Bar share={c.share} isBest={c.isBest} />}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              {hiddenCount > 0 && (
                <button
                  type="button"
                  onClick={() => onToggleExpanded(block.id)}
                  className="w-full px-[13px] py-[11px] text-center text-[12.5px] font-semibold text-[#1B6FD6]"
                >
                  {isOpen ? "Hide extra stats" : `Show all stats (+${hiddenCount})`}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CompareGridMobile;
